import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GroupReliefOptimiserCanvas from "./components/GroupReliefOptimiserCanvas";
import useGroupReliefOptimiserWorkspace from "./hooks/useGroupReliefOptimiserWorkspace";
import { basisPeriod, MAX_COMPANIES } from "./domain/preparation";
import ProcessGuideDialog from "../../../library/Dialog/ProcessGuideDialog";
import { groupReliefGuideSteps } from "./components/groupReliefGuideSteps";
import { formatAmount } from "../../../utils/hooks/dataHandler";

export default function GroupReliefOptimiserWorkspace() {
  const w = useGroupReliefOptimiserWorkspace();
  const basis = basisPeriod(w.settings.commonFinancialYearEnd);
  const [attempted, setAttempted] = useState(false);
  const [caseAnchor, setCaseAnchor] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const content = useRef(null);
  useEffect(() => {
    setAttempted(false);
    content.current?.scrollTo?.(0, 0);
  }, [w.step, w.activeId]);
  const allConfirmed =
    w.selected.length >= 2 && w.confirmedCount === w.selected.length;
  const pendingExternalCount = w.selected.filter(
    (company) => company.isExternal && company.externalPending,
  ).length;
  const advance = () => {
    setAttempted(true);
    if (w.step === 0) w.goToCompanies();
    else if (w.step === 1) w.goToDetails();
    requestAnimationFrame(() =>
      content.current?.querySelector('[aria-invalid="true"]')?.focus()
    );
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 1.8,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
              placeItems: "center",
              flexShrink: 0,
              display: { xs: "none", sm: "grid" },
            }}
          >
            <AccountTreeIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='h6' fontWeight={800} sx={{ lineHeight: 1.25 }}>
              Group Relief Optimiser
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Prepare, review and optimise group relief.
            </Typography>
          </Box>
          <Button
            variant='outlined'
            endIcon={<ExpandMoreIcon />}
            onClick={(event) => setCaseAnchor(event.currentTarget)}
            aria-expanded={Boolean(caseAnchor)}
            aria-controls={caseAnchor ? "group-relief-case-summary" : undefined}
            sx={{ flexShrink: 0, minHeight: 36, textTransform: "none" }}
          >
            Case & Guide
          </Button>
        </Stack>
      </Box>
      <Popover
        id='group-relief-case-summary'
        open={Boolean(caseAnchor)}
        anchorEl={caseAnchor}
        onClose={() => setCaseAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { width: 360, maxWidth: "calc(100vw - 32px)", p: 2 },
        }}
      >
        <Stack
          component='section'
          aria-label='Current case settings'
          spacing={1.5}
        >
          <Typography fontWeight={700}>Your case settings</Typography>
          <Typography variant='body2' color='text.secondary'>
            A case brings together companies for one Group Relief calculation.
            These settings apply to all selected companies.
          </Typography>
          <Box
            component='dl'
            sx={{
              m: 0,
              "& dt": { color: "text.secondary", fontSize: "0.875rem" },
              "& dd": {
                m: 0,
                mb: 1,
                fontWeight: 600,
                overflowWrap: "anywhere",
              },
            }}
          >
            <dt>Year of Assessment</dt>
            <dd>{w.settings.yearOfAssessment || "Not set"}</dd>
            <dt>Financial year end</dt>
            <dd>{w.settings.commonFinancialYearEnd || "Not set"}</dd>
            <dt>Basis period</dt>
            <dd>
              {basis
                ? `${basis.start} to ${basis.end} (${basis.days} days)`
                : "Choose a financial year end"}
            </dd>
            <dt>CIT rebate</dt>
            <dd>
              {w.settings.rebatePercentage || "0"}% · Cap SGD{" "}
              {formatAmount(w.settings.rebateCap || "0")}
            </dd>
          </Box>
          <Stack direction='row' gap={1} flexWrap='wrap'>
            <Button
              variant='outlined'
              onClick={() => {
                setCaseAnchor(null);
                w.setStep(0);
              }}
            >
              Edit case setup
            </Button>
            <Button
              variant='outlined'
              startIcon={<MenuBookOutlinedIcon />}
              onClick={() => {
                setCaseAnchor(null);
                setGuideOpen(true);
              }}
              sx={{
                textTransform: "none",
                borderColor: "divider",
                color: "text.secondary",
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              User Guide
            </Button>
          </Stack>
        </Stack>
      </Popover>
      <ProcessGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        title='Group Relief Optimiser user guide'
        titleIcon={AccountTreeIcon}
        steps={groupReliefGuideSteps}
      />
      <Box
        component='section'
        ref={content}
        sx={{ flex: 1, minHeight: 0, overflow: "auto", p: { xs: 2, md: 3 } }}
      >
        {w.notice && (
          <Alert severity='info' sx={{ mb: 2 }}>
            {w.notice}
          </Alert>
        )}
        <GroupReliefOptimiserCanvas workspace={w} attempted={attempted} />
      </Box>
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1,
          "@media (max-height: 720px)": { py: 0.5, px: 2 },
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {w.step === 1 &&
          (w.loading || w.selectionIssues.length ? (
            <Alert
              severity='info'
              role='status'
              sx={{
                flex: "1 1 220px",
                minWidth: 0,
                py: 0,
                px: 1,
                bgcolor: "transparent",
                "& .MuiAlert-icon": { py: 0.5, mr: 1 },
                "& .MuiAlert-message": { py: 0.5 },
              }}
            >
              {w.loading
                ? "Checking accessible computations…"
                : pendingExternalCount
                  ? `Upload ${pendingExternalCount} matching external compan${
                      pendingExternalCount === 1 ? "y" : "ies"
                    } before continuing.`
                  : "Remove or replace companies that no longer match."}
            </Alert>
          ) : (
            <Chip
              role='status'
              variant='outlined'
              color={w.selectedIds.length >= 2 ? "primary" : "default"}
              label={
                w.selectedIds.length >= 2
                  ? `${w.selectedIds.length} / ${MAX_COMPANIES} companies selected`
                  : "Select at least 2 companies"
              }
              sx={{ flexShrink: 0, fontWeight: 600 }}
            />
          ))}
        <Stack
          direction='row'
          gap={1}
          flexWrap='wrap'
          alignItems='center'
          justifyContent='flex-end'
          sx={{
            ml: "auto",
            maxWidth: "100%",
            "& .MuiButton-root": {
              minHeight: 36,
              py: 0.5,
              textTransform: "none",
            },
            "@media (max-height: 720px)": { gap: 0.5 },
          }}
        >
          {w.step === 3 && (
            <Button
              onClick={() => {
                w.setShowResults(false);
                w.setReviewOpen(false);
                w.setStep(0);
              }}
              sx={{ textTransform: "none" }}
            >
              Edit case setup
            </Button>
          )}
          {w.step > 0 && w.step !== 3 && (
            <Button onClick={() => w.setStep(w.step - 1)}>Back</Button>
          )}
          {w.step < 2 ? (
            <Button
              variant='contained'
              disableElevation
              endIcon={<ArrowForwardIcon />}
              onClick={advance}
              disabled={w.step === 1 && !w.canContinue}
            >
              {w.step === 0
                ? "Continue to Companies"
                : "Continue to Company Details"}
            </Button>
          ) : w.step === 3 ? (
            <Button variant='contained' onClick={() => w.setStep(2)}>
              Edit company details
            </Button>
          ) : null}

          {w.step === 2 && (
            <Button
              variant={allConfirmed ? "contained" : "outlined"}
              endIcon={<ArrowForwardIcon />}
              onClick={w.goToReview}
            >
              Review & Calculate
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
